import { memo, useState, useCallback } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";

import { EposlovanjeEditCardProps } from "./types";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { EPOSLOVANJE_EDIT_FORM_SCHEMA } from "./validation";
import { validateYup } from "services/forms/validation/validation";

const EposlovanjeEditCard: React.FC<EposlovanjeEditCardProps> = (props) => {
  const { settings, setSettings, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      settings,
      EPOSLOVANJE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Add API call to save settings
      if (onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setSettings,
    settings
  );

  const environmentOptions = [
    { value: "sandbox", text: t("admin.integration.eposlovanje.environment.sandbox") },
    { value: "production", text: t("admin.integration.eposlovanje.environment.production") },
  ];

  const handleEnvironmentChange = useCallback(
    (value: string | number | null) => {
      setSettings((prev) => ({
        ...prev,
        environment: value as "production" | "sandbox",
      }));
    },
    [setSettings]
  );

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="flex gap-3 h-full">
            <form noValidate className="text-left flex-1 " onSubmit={handleFormSubmit}>
              <div className="p-4">
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="apiKey"
                    label="API ključ"
                    onChange={handleTextFieldChange}
                    value={settings.apiKey}
                    error={errors.apiKey}
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="companyVatId"
                    label="OIB tvrtke (11 znamenki)"
                    onChange={handleTextFieldChange}
                    value={settings.companyVatId}
                    error={errors.companyVatId}
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="businessUnit"
                    label="Poslovni prostor"
                    onChange={handleTextFieldChange}
                    value={settings.businessUnit}
                    error={errors.businessUnit}
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="software"
                    label="Software"
                    onChange={handleTextFieldChange}
                    value={settings.software}
                    error={errors.software}
                  />
                </div>
                <div className="mb-3">
                  <Dropdown
                    name="environment"
                    label="Okruženje"
                    options={environmentOptions}
                    onChange={handleEnvironmentChange}
                    value={settings.environment}
                    error={errors.environment}
                  />
                </div>
              </div>
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={() => onClose()}
                />
                <Button
                  variant="primary"
                  children={t("admin.save")}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(EposlovanjeEditCard);
